import { Row, Col } from "react-bootstrap";
import SimpleCard from "../../common/simple-card";
import { FC, useState, useMemo, useEffect } from "react";
import { joinTimeParts } from "lib/utils/date-utils";
import SimpleList from "lib/components/common/simple-list";
import { useSessionBooking } from "lib/contexts/session-booking-context";
import useApi from "lib/hooks/useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import SessionBookingDetailsTile from "../booking-session-details-tile";

interface AppointmentBooking {
  fromTime: { hour: number; minute: number };
  toTime: { hour: number; minute: number };
  isBooked?: boolean;
}
interface ClinicAppointmentBooking {
  fromTime: { hour: number; minute: number };
  toTime: { hour: number; minute: number };
  isBooked?: boolean;
  day: string;
}

interface PatientConfirmAppointmentProps {
  bookingSummary?: SessionBookingDetails;
  onSlotSelected?: (slot: AppointmentBooking) => void;
  onDateSelected?: (date: Date) => void;
  onBookingConfirmed?: (appointment: SessionBookingDetails) => void;
}

// Utility to generate next 90 days
// const generateNext90Days = () => {
//   const today = new Date();
//   return Array.from({ length: 90 }, (_, i) => {
//     const d = new Date(today);
//     d.setDate(today.getDate() + i);
//     return d.toDateString();
//   });
// };

const generateNext90Days = () => {
  const today = new Date();

  const removeDates = [
    "Dec 23 2025",
    "Dec 24 2025",
    "Dec 25 2025",
    "Dec 26 2025",
    "Dec 28 2025",
    "Dec 31 2025",
    "Jan 01 2026",
    "Jan 02 2026",
    "Jan 04 2026"
  ].map(d => new Date(d).toDateString());

  return Array.from({ length: 90 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d.toDateString();
  }).filter(date => !removeDates.includes(date));
};


const PatientConfirmTherapySession: FC<PatientConfirmAppointmentProps> = ({
  bookingSummary,
  onDateSelected,
  onSlotSelected,
  onBookingConfirmed,
}) => {
  const [selectedDateIndex, setSelectedDateIndex] = useState<number | null>(null);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [selectedSlotDetails, setSelectedSlotDetails] = useState<AppointmentBooking | null>(null);
  const updatedDates = useMemo(() => generateNext90Days(), []);
  const [practitionerDates, setPractitionerDates] = useState<string[]>(updatedDates);
  const { sessionBookingState, updateSessionBookingState } = useSessionBooking();
  const { getData, postData, loading: apiLoading } = useApi<any>();
  const [therapyDetails, setTherapyDetails] = useState<any>(null);
  const [clinicDetails, setClinicDetails] = useState<any>(null);
  const [bookedSessions, setBookedSessions] = useState<any[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [bookingSlots, setBookingSlots] = useState<AppointmentBooking[]>([]);


  // Generate slots based on therapy duration
  function generateSlotsByDuration(duration: number): AppointmentBooking[] {
    const slots: AppointmentBooking[] = [];
    let hour = 8, minute = 0;
    while (hour < 20) {
      const from = { hour, minute };
      const to = addMinutes(from, duration);
      slots.push({ fromTime: { ...from }, toTime: { ...to } });
      hour = to.hour;
      minute = to.minute;
    }
    return slots;
  }

  // Generate all slots for all days based on availableHours
  function generateSlotsByClinicHours(
    availableHours: { day: string; fromTime: string; toTime: string }[],
    duration: number
  ) {
    const slots: ClinicAppointmentBooking[] = [];

    availableHours.forEach((dayInfo) => {
      let from = parseTime(dayInfo.fromTime);
      const end = parseTime(dayInfo.toTime);

      while (true) {
        const to = addMinutes(from, duration);

        // Stop if surpass end-of-day
        const toAfterEnd =
          to.hour > end.hour ||
          (to.hour === end.hour && to.minute > end.minute);

        if (toAfterEnd) break;

        slots.push({
          fromTime: { ...from },
          toTime: { ...to },
          isBooked: false,
          day: dayInfo.day, // Add day info (helpful for filtering)
        });

        from = to;
      }
    });

    return slots;
  }

  // Convert "HH:mm" to { hour, minute }
  function parseTime(timeStr: string) {
    const [hour, minute] = timeStr.split(":").map(Number);
    return { hour, minute };
  }

  // Helper to add minutes to a time
  function addMinutes(time: { hour: number; minute: number }, mins: number) {
    const total = time.hour * 60 + time.minute + mins;
    return { hour: Math.floor(total / 60), minute: total % 60 };
  }

  // Fetch therapy details and booked sessions on page load (only once)
  useEffect(() => {
    if (!sessionBookingState.therapyId) return;
    let isMounted = true;
    postData(
      API_ROUTES.THERAPIES.base + "/details-and-booked-sessions",
      { therapyId: sessionBookingState.therapyId, clinicId: sessionBookingState.clinicId },
      (response) => {
        // Corrected: API returns { success, message, data }, so use response.data.data
        const apiData = response;
        if (!isMounted) return;
        if (response.error) {
          setApiError(response.error);
        } else if (apiData) {
          setTherapyDetails(apiData.therapy?.[0] || null);
          setClinicDetails(apiData.clinic?.[0] || null);
          setBookedSessions(apiData.therapySessionBooked || []);
          // Generate slots based on therapy duration
          const duration = apiData.therapy?.[0]?.duration || 30;
          setBookingSlots(generateSlotsByDuration(duration));
        } else {
          setTherapyDetails(null);
          setClinicDetails(null);
          setBookedSessions([]);
          setBookingSlots([]);
        }
      }
    );
    return () => { isMounted = false; };
  }, [sessionBookingState.therapyId]);

  const filteredDates = useMemo(() => {
    if (!clinicDetails) return practitionerDates;

    if (!clinicDetails.sundayClose) return practitionerDates;

    return practitionerDates.filter((dateStr) => {
      const d = new Date(dateStr);
      return d.getDay() !== 0; // Remove Sundays
    });
  }, [clinicDetails?.sundayClose, practitionerDates]);

  // All slot labels (only for selected date)
  // Filter only valid slots based on current date/time
  const filteredSlots = useMemo(() => {
    if (selectedDateIndex === null) return [];

    const selectedDate = new Date(practitionerDates[selectedDateIndex]);
    const now = new Date();

    const isToday =
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getDate() === now.getDate();

    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    const isDec2025 =
      selectedDate.getFullYear() === 2025 && selectedDate.getMonth() === 11;
    const isRestrictedDay =
      isDec2025 && [27, 29, 30].includes(selectedDate.getDate());

    return bookingSlots.filter((slot) => {
      const slotHour = slot.fromTime.hour;
      const slotMinute = slot.fromTime.minute;

      // console.log("Evaluating slot:", slot,isRestrictedDay);

      if (!isToday && !isRestrictedDay) return true;
      if (isRestrictedDay) {
        if (slotHour < currentHour) return false;
        if (slotHour === currentHour && slotMinute < currentMinute) return false;
        return (
          slotHour >= 10 &&
          (
            slotHour < 13 ||
            (slotHour === 13 && slotMinute < 30)
          )
        );
      }
      if (isToday) {
        if (slotHour > currentHour) return true;
        if (slotHour === currentHour && slotMinute > currentMinute) return true;
        // console.log("Filtering out past slot for today:", slot);
        return false;
      }





      // console.log("Applying restricted day filter for slot:", slot);



      return true;
    });
  }, [bookingSlots, selectedDateIndex, practitionerDates]);




  const allSlots = useMemo(() => {
    return filteredSlots.map(
      (slot) =>
        `${joinTimeParts(String(slot.fromTime.hour), String(slot.fromTime.minute))} - ${joinTimeParts(
          String(slot.toTime.hour),
          String(slot.toTime.minute)
        )}`
    );
  }, [filteredSlots]);

  // Compute disabled slots for the selected date
  const disabledSlots = useMemo(() => {
    if (selectedDateIndex === null || !bookedSessions.length) return [];
    const selectedDate = new Date(filteredDates[selectedDateIndex]);
    return bookedSessions
      .filter((session: any) => {
        const sessionDate = new Date(session.fromTime);
        return (
          sessionDate.getFullYear() === selectedDate.getFullYear() &&
          sessionDate.getMonth() === selectedDate.getMonth() &&
          sessionDate.getDate() === selectedDate.getDate()
        );
      })
      .map((session: any) => {
        const from = new Date(session.fromTime);
        const to = new Date(from.getTime() + (session.duration || 30) * 60000);
        return `${joinTimeParts(String(from.getUTCHours()), String(from.getUTCMinutes()))} - ${joinTimeParts(String(to.getUTCHours()), String(to.getUTCMinutes()))}`;
      });
  }, [bookedSessions, selectedDateIndex, filteredDates]);


  // When a date is selected, show slots for that date
  const handleDateSelected = (itemIndex: number) => {
    setSelectedDateIndex(itemIndex);
    setSelectedSlotIndex(null);
    setSelectedSlotDetails(null);

    const selectedDate = new Date(filteredDates[itemIndex]);

    // --- Get day name (e.g., Monday) ---
    const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = weekdayNames[selectedDate.getDay()];

    // --- Fetch clinic hours for the selected day ---
    const todaysHours = clinicDetails?.availableHours?.filter(
      (h: any) => h.day === dayName
    ) || [];

    // --- Generate slots only for that day based on therapy duration ---
    if (therapyDetails) {
      const duration = therapyDetails.duration || 30;

      const daySlots = todaysHours.length
        ? generateSlotsByClinicHours(todaysHours, duration)
        : [];

      console.log("Generated Slots for Selected Day:", daySlots);

      setBookingSlots(daySlots);
    }

    // Callback
    if (onDateSelected) {
      onDateSelected(selectedDate);
    }
  };

  // When a slot is selected, show details for that slot
  const handleSlotSelected = (itemIndex: number) => {
    const slot = filteredSlots[itemIndex];
    if (!slot) return;

    setSelectedSlotIndex(itemIndex);
    setSelectedSlotDetails(slot);

    if (therapyDetails && selectedDateIndex !== null) {
      const baseDate = new Date(filteredDates[selectedDateIndex]);
      const localDate = new Date(Date.UTC(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        slot.fromTime.hour,
        slot.fromTime.minute
      ))
      const isoString = localDate.toISOString();

      updateSessionBookingState({
        ...sessionBookingState,
        clinicId: clinicDetails.id || "",
        date: isoString,
        time: isoString,
      });
    }

    if (onSlotSelected) {
      onSlotSelected(slot);
    }
  };

  // Book session handler
  const handleBookingConfirmed = async () => {
    if (!therapyDetails || !clinicDetails || !sessionBookingState?.patientId) return;
    const fromTime = sessionBookingState.date || sessionBookingState.time;
    const payload = {
      therapyId: therapyDetails.id,
      patientId: sessionBookingState.patientId,
      clinicId: clinicDetails.id,
      fromTime: fromTime,
      duration: therapyDetails.duration || 30,
    };
    postData(
      API_ROUTES.PATIENT.bookSession.replace("{{PATIENT_ID}}", sessionBookingState.patientId),
      payload,
      (data: any) => {
        console.log("Booking response: ", data);
        if (data?.checkoutUrl) {
          // If Stripe checkoutUrl is present, redirect
          window.location.href = data.checkoutUrl;
          return;
        } else {
          setApiError(data?.message || "Booking failed");
        }
      },
      (err: any) => setApiError("Booking failed. Please try again.")
    );
  };

  return (
    <SimpleCard>
      {apiLoading && <div>Loading therapy details...</div>}
      {apiError && <div className="text-danger">{apiError}</div>}
      <Row>
        <Col xs={12} md={4} className="mb-4 mb-md-0">
          <h5>Available Dates</h5>
          <small className="text-muted">
            Select the date to see available slots
          </small>
          <SimpleList
            items={filteredDates}
            className="auto-scroll-350 mt-3"
            selectedItemIndex={selectedDateIndex}
            onItemClick={handleDateSelected}
          />
        </Col>
        <Col xs={12} md={4} className="mb-4 mb-md-0">
          <h5>Available Slots</h5>
          <small className="text-muted">Select the slot to book</small>
          {selectedDateIndex !== null ? (
            <SimpleList
              items={allSlots as string[]}
              disabledItems={disabledSlots as string[]}
              className="auto-scroll-350 mt-3"
              selectedItemIndex={selectedSlotIndex}
              onItemClick={handleSlotSelected}
            />
          ) : (
            <div className="text-muted mt-3">Please select a date to view slots.</div>
          )}
        </Col>
        <Col xs={12} md={4}>
          <h5>Booking Summary</h5>
          <small className="text-muted">
            Review your booking details and click "Book Appointment" to confirm.
          </small>
          <div className="mt-3">
            <SessionBookingDetailsTile
              bookingDetails={
                bookingSummary ||
                (selectedDateIndex !== null && selectedSlotDetails && therapyDetails
                  ? {
                    clinicId: clinicDetails.id,
                    clinicName: clinicDetails.name || "",
                    patientId: sessionBookingState?.patientId || "",
                    patientName: sessionBookingState?.patientName || "",
                    therapyId: therapyDetails.id,
                    therapyName: therapyDetails.name,
                    fromTime: new Date(
                      new Date(practitionerDates[selectedDateIndex]).getFullYear(),
                      new Date(practitionerDates[selectedDateIndex]).getMonth(),
                      new Date(practitionerDates[selectedDateIndex]).getDate(),
                      selectedSlotDetails.fromTime.hour,
                      selectedSlotDetails.fromTime.minute
                    ).toISOString(),
                    duration: therapyDetails.duration || 30,
                    totalCost: therapyDetails.price || 0,
                    status: "pending"
                  }
                  : undefined)
              }
              cancelAppointmentTitle="Book Session"
              onCancelAppointment={handleBookingConfirmed}
            />
          </div>
          {bookingSummary && (
            <div className="mt-3">
              <div className="mt-3">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleBookingConfirmed}
                >
                  Book Session
                </button>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </SimpleCard>
  );
};

export default PatientConfirmTherapySession;
